/**
 * Simplecast Engagement Tracker Class
 */
(function () {
	try {
		/** Start - class code starts **/
		class SimplecastEngagementTracker {
			constructor(player, episodeTitle) {
				this.watchedSeconds = new Set();
				this.milestonesFired = {
					25: false,
					50: false,
					75: false,
					100: false,
				};
				this.last10SecFired = 0;
				this.episodeTitle = episodeTitle;
				this.player = player;
				this.player.one('play', () => {
					this.sendToDatalayer({
						event: 'video_start',
						eventAction: 'Video Start',
						milestone: '',
						seconds: 0,
						percentage: 0,
						eventType: 'play',
					});
				});
				this.player.on('timeupdate', () => this.handleTimeUpdate());
				this.player.on('ended', () => this.handleEnded());
			}

			/**
			 * Returns the base data layer object containing information about media playback and related metadata.
			 *
			 * @return {Object} An object containing the media type, media source, event category, event label, and custom dimensions such as duration and video name.
			 */
			getBaseDatalayerData() {
				return {
					mediaType: 'audio',
					mediaSource: 'simplecast',
					eventCategory: 'Video',
					eventLabel: this.episodeTitle,
					customDimensions: {
						duration: this.player.duration() || 0,
						// eslint-disable-next-line camelcase
						video_name: this.episodeTitle,
					},
				};
			}

			/**
			 * Sends data to the data layer for tracking events.
			 *
			 * @param {Object} params - The parameters for the data layer event.
			 * @param {string} params.event - The name of the event to send.
			 * @param {string} params.eventAction - The action associated with the event.
			 * @param {string} [params.milestone=''] - The milestone related to the event.
			 * @param {number} [params.seconds=0] - The number of seconds viewed.
			 * @param {number} [params.percentage=0] - The percentage of content viewed.
			 * @param {string} [params.eventType=''] - The type of event (e.g., 'play').
			 * @return {void}
			 */
			sendToDatalayer({
				event,
				eventAction,
				milestone = '',
				seconds = 0,
				percentage = 0,
				eventType = '',
			}) {
				if (!event || !eventAction) return;
				const data = {
					event,
					eventAction,
					...this.getBaseDatalayerData(),
				};
				if (eventType !== 'play') {
					data.customDimensions = {
						...data.customDimensions,
						milestone,
						// eslint-disable-next-line camelcase
						seconds_viewed: seconds,
						// eslint-disable-next-line camelcase
						percent_viewed: percentage,
					};
				}
				window.hmUtils?.setDataLayerTarget?.(data);
			}

			/**
			 * Handles the time update event for the video player, tracking the watched seconds,
			 * calculating the percentage of the video viewed, and firing events when certain
			 * engagement milestones are reached.
			 *
			 * @return {void} This method does not return a value.
			 */
			handleTimeUpdate() {
				const duration = this.player.duration();
				if (!duration) return;
				const baseVideoEngagementData = {
					event: 'video_engagement',
					eventAction: 'Video Engagement',
					milestone: '',
				};
				const currentSecond = Math.floor(this.player.currentTime());
				this.watchedSeconds.add(currentSecond);
				const actualSecondsViewed = this.watchedSeconds.size;
				const actualPercentageViewed =
					(actualSecondsViewed / duration) * 100;
				if (
					actualSecondsViewed > 0 &&
					actualSecondsViewed % 10 === 0 &&
					actualSecondsViewed !== this.last10SecFired
				) {
					this.last10SecFired = actualSecondsViewed;
					this.sendToDatalayer({
						...baseVideoEngagementData,
						seconds: actualSecondsViewed,
						percentage: actualPercentageViewed,
					});
				}
				[25, 50, 75].forEach((milestone) => {
					if (
						actualPercentageViewed >= milestone &&
						!this.milestonesFired[milestone]
					) {
						this.milestonesFired[milestone] = true;
						this.sendToDatalayer({
							...baseVideoEngagementData,
							milestone,
							seconds: actualSecondsViewed,
							percentage: actualPercentageViewed,
						});
					}
				});
			}

			/**
			 * Handles the event when a video ends. It checks if the 100 percent milestone has been fired.
			 * If not, it calculates video engagement metrics and sends them to the data layer.
			 * Additionally, it resets the state after processing.
			 *
			 * @return {void} This method does not return a value.
			 */
			handleEnded() {
				if (!this.milestonesFired[100]) {
					this.milestonesFired[100] = true;
					const duration = this.player.duration();
					const actualPercentage = duration
						? (this.watchedSeconds.size / duration) * 100
						: 0;
					this.sendToDatalayer({
						event: 'video_engagement',
						eventAction: 'Video Engagement',
						milestone: 100,
						seconds: this.watchedSeconds.size,
						percentage: actualPercentage,
					});
				}
				this.resetState();
			}

			/**
			 * Resets the state of the object by clearing watched seconds, resetting milestone flags,
			 * and initializing the last triggered time for a 10-second interval.
			 *
			 * @return {void} Does not return a value.
			 */
			resetState() {
				this.watchedSeconds.clear();
				this.milestonesFired = {
					25: false,
					50: false,
					75: false,
					100: false,
				};
				this.last10SecFired = 0;
			}
		}
		/** End - class code ends **/

		/**
		 * Initializes the Simplecast audio player with the specified configuration.
		 *
		 */
		const initPlayer = () => {
			const elements = document.querySelectorAll(
				'.video-js[id^="simplecast-audio-player-"]',
			);
			elements.forEach((element) => {
				const playerId = element.id;
				const player = videojs(playerId, {
					controlBar: {
						skipButtons: {
							forward: 30,
							backward: 30,
						},
						pictureInPictureToggle: false,
					},
				});
				let episodeTitle =
					document
						.querySelector(
							`#episode-title-${playerId.replace('simplecast-audio-player-', '')}`,
						)
						?.textContent.trim() || '';
				new SimplecastEngagementTracker(player, episodeTitle);
			});
		};
		document.addEventListener('DOMContentLoaded', initPlayer);
	} catch (e) {
		console.error(e); // eslint-disable-line
	}
})();
